import { Button } from '@/components/shared/buttons/button';
import { Text } from '@/components/shared/element/text';
import { Box } from '@/components/shared/layout/box';
import { styled } from '@/stitches.config';
import { IHeadline, IMy24Theme } from '@/types/shared';
import { ITracking } from '@/types/tracking';

const HeadlinePreTitle = styled(Text, {
  marginBottom: '$space-2',
  variants: {
    decorate: {
      true: {
        position: 'relative',
        display: 'flex',
        alignItems: 'flex-end',
        columnGap: '$space-1',
        marginBottom: '$space-3',
        '&::before': {
          content: '""',
          bc: '$primary',
          width: 'calc(20px + (28 - 20) * ((100vw - 414px) / (1024 - 414)))',
          display: 'block',
          aspectRatio: '28 / 42',
          clipPath: 'polygon(65% 0%, 100% 0%, 35% 100%, 0% 100%)',
          '@lg': {
            width: 28,
          },
        },
      },
    },
  },
});

const HeadlineButtonWrap = styled('div', {
  display: 'flex',
  rowGap: '$space-3',
  columnGap: '$space-4',
  '@xs': {
    columnGap: '$space-6',
  },
  variants: {
    wrap: {
      noWrap: {
        flexWrap: 'nowrap',
      },
      wrap: {
        flexWrap: 'wrap',
      },
      wrapReverse: {
        flexWrap: 'wrap-reverse',
      },
    },
    align: {
      start: {
        alignItems: 'flex-start',
      },
      center: {
        alignItems: 'center',
      },
      end: {
        alignItems: 'flex-end',
      },
      stretch: {
        alignItems: 'stretch',
      },
      baseline: {
        alignItems: 'baseline',
      },
    },
    justify: {
      start: {
        justifyContent: 'flex-start',
      },
      center: {
        justifyContent: 'center',
      },
      end: {
        justifyContent: 'flex-end',
      },
      between: {
        justifyContent: 'space-between',
      },
      around: {
        justifyContent: 'space-around',
      },
      evenly: {
        justifyContent: 'space-evenly',
      },
    },
  },
  defaultVariants: {
    wrap: 'wrap',
    align: 'center',
  },
});

const HeadlineDescription = styled(Text, {});

const HeadlineTitle = styled(Text, {
  [`& ~ ${HeadlineDescription}`]: {
    marginTop: '$space-3',
  },
  variants: {
    size: {
      'text-h1': {
        '& strong': {
          display: 'block',
          fontSize: '50%',
          fontFamily: '$eurothai',
          fontWeight: 'normal',
          fontStyle: 'normal',
          lineHeight: '100%',
          color: '$gray700',
        },
      },
    },
  },
});

const StyledHeadline = styled('div', {
  variants: {
    type: {
      standard: {
        [`& ${HeadlineButtonWrap}`]: {
          marginTop: '$space-10',
          '@maxlg': {
            display: 'none',
          },
        },
      },
      'two-row': {
        '@lg': {
          display: 'grid',
          gridTemplateColumns: 'repeat(12, 1fr)',
          gap: '$space-5',
        },

        [`& ${HeadlineButtonWrap}`]: {
          marginTop: '$space-10',
          '@maxlg': {
            display: 'none',
          },
        },
      },
      'one-row': {
        marginBottom: 32,
        '@md': {
          marginBottom: 40,
        },
        '@lg': {
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          gap: '$space-5',
        },
        [`& ${HeadlineButtonWrap}`]: {
          '@maxlg': {
            display: 'none',
          },
          '@lg': {
            flexBasis: 'auto',
            justifyContent: 'flex-end',
          },
        },
      },
    },
  },
});

interface HeadlineProps
  extends IHeadline,
    React.ComponentProps<typeof StyledHeadline> {
  theme?: IMy24Theme;
  type?: 'standard' | 'two-row' | 'one-row';
  tracking?: ITracking;
}

const PreTitle = (props: HeadlineProps) => {
  const preTitleVariant = () => {
    switch (props?.theme) {
      case 'light':
        if (props?.type === 'standard') return 'text-gray-darkest';
        return 'text-gray';
      case 'dark':
        return 'text-white';
      default:
        return 'text-gray-darkest';
    }
  };

  return (
    <HeadlinePreTitle
      as="h2"
      size="text-sub-h2"
      font="bold"
      variant={preTitleVariant()}
      decorate={props?.type === 'standard' || props?.decorate}
      data-test="headline_preTitle"
      wrap
      dangerouslySetInnerHTML={{
        __html: props?.preTitle as string,
      }}
    />
  );
};

const Title = (props: HeadlineProps) => {
  const titleVariant = () => {
    switch (props.theme) {
      case 'light':
        if (props.type === 'standard') return 'text-red';
        return 'text-gray-darkest';
      case 'dark':
        if (props.type === 'standard') return 'text-red';
        return 'text-white';
      default:
        return 'text-gray-darkest';
    }
  };
  return (
    <HeadlineTitle
      as="h2"
      size="text-h1"
      line-height="75"
      variant={titleVariant()}
      theme={props?.theme}
      wrap
      dangerouslySetInnerHTML={{
        __html: props?.title as string,
      }}
      data-test="headline_title"
      className={
        'headline-title' + (props.className ? ' ' + props.className : '')
      }
      css={{
        '&.mojito': {
          '.text-lime': {
            color: '$lime',
            fontSize: `clamp(32px, ${-360 * ((40 - 32) / 920) + 32}px + ${
              ((40 - 32) / 920) * 100
            }vw, 40px)`,
          },
        },
      }}
    />
  );
};

const Description = (props: HeadlineProps) => {
  const descriptionVariant = () => {
    switch (props.theme) {
      case 'light':
        return 'text-gray-darkest';
      case 'dark':
        return 'text-white';
      default:
        return 'text-gray-darkest';
    }
  };
  return (
    <HeadlineDescription
      as="div"
      size="text-body-1"
      variant={descriptionVariant()}
      wrap
      dangerouslySetInnerHTML={{
        __html: props?.description as string,
      }}
      data-test="headline_description"
      className={props.className}
    />
  );
};
const Buttons = (props: HeadlineProps) => {
  return (
    <HeadlineButtonWrap>
      {props?.buttons?.map((button, i) => (
        <Button
          key={i}
          href={button?.url}
          target={button?.target}
          border={button?.border}
          label={button?.label}
          icon={button?.icon}
          variant={button?.color}
          size={button?.size}
          isMinWidth={button?.isMinWidth}
          tracking={{
            dataTrack: props?.tracking?.dataTrack as string,
            dataTrackSection: props?.tracking?.dataTrackSection,
            dataTrackText: button?.label,
            dataTrackUrl: button?.url,
            dataTrackValue: props?.tracking?.dataTrackValue,
          }}
          test={{
            dataTest: `headline_ctaButton_${i + 1}`,
          }}
        />
      ))}
    </HeadlineButtonWrap>
  );
};

export const Headline: React.FC<HeadlineProps> = ({
  theme = 'light',
  type = 'standard',
  ...props
}) => {
  const options = {
    type: type,
  };

  return (
    <>
      {!!props?.title && (
        <StyledHeadline
          {...options}
          data-test="headline"
          className={props.className}
        >
          {type === 'one-row' && (
            <>
              <Box
                css={{
                  '@lg': {
                    flexGrow: 1,
                    flexBasis: 0,
                    maxWidth: '100%',
                  },
                }}
              >
                {!!props?.preTitle && (
                  <PreTitle theme={theme} type={type} {...props} />
                )}
                {!!props?.title && (
                  <Title theme={theme} type={type} {...props} />
                )}
                {!!props?.description && (
                  <Description theme={theme} type={type} {...props} />
                )}
              </Box>
              {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
              {/* @ts-ignore */}
              {props?.buttons?.length > 0 && (
                <Buttons theme={theme} type={type} {...props} />
              )}
            </>
          )}
          {type === 'two-row' && (
            <>
              <Box
                className={props.className}
                css={{
                  '@lg': {
                    gridColumn: '1 / span 6',
                  },
                  '@xl': {
                    gridColumn: '2 / span 5',
                  },
                }}
              >
                {!!props?.preTitle && (
                  <PreTitle theme={theme} type={type} {...props} />
                )}
                {!!props?.title && (
                  <Title theme={theme} type={type} {...props} />
                )}
              </Box>
              {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
              {/* @ts-ignore */}
              {!!(props?.description || props?.buttons?.length > 0) && (
                <Box
                  className={props.className}
                  css={{
                    '@lg': {
                      gridColumn: '7 / span 6',
                      gridRow: '2 / auto',
                    },
                    '@xl': {
                      gridColumn: '7 / span 5',
                    },
                    '@maxlg': {
                      marginTop: '$space-5',
                    },
                  }}
                >
                  {!!props?.description && (
                    <Description theme={theme} type={type} {...props} />
                  )}
                  {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
                  {/* @ts-ignore */}
                  {props?.buttons?.length > 0 && (
                    <Buttons theme={theme} type={type} {...props} />
                  )}
                </Box>
              )}
            </>
          )}
          {type === 'standard' && (
            <>
              {!!props?.preTitle && (
                <PreTitle theme={theme} type={type} {...props} />
              )}
              {!!props?.title && <Title theme={theme} type={type} {...props} />}
              {!!props?.description && (
                <Description theme={theme} type={type} {...props} />
              )}
              {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
              {/* @ts-ignore */}
              {props?.buttons?.length > 0 && (
                <Buttons theme={theme} type={type} {...props} />
              )}
            </>
          )}
        </StyledHeadline>
      )}
    </>
  );
};

Headline.displayName = 'SharedMY24Headline';
